import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import {
    Box,
    Button,
    Heading,
    SubtleText,
    createButtonPrimaryColorConfig,
    createButtonTertiaryColorConfig,
} from '@99designs/emu';
import { breakpoints } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { useRouterContext } from '@99designs/routing';
import { ContentBody, DesignFigure, Page } from '@99designs/spa-common';
import { ButtonContainer } from '@99designs/spa-common';
import Poopskies from '../../images/baby_figzy88.jpg';
import LostDog from '../../images/dog_alinasink.jpg';
import Elephant from '../../images/elephant_ganjarelex.jpg';
import ValentinoBaptista from '../../images/valentino_baptista.png';
import { useAppConfig } from '../AppConfigContext';

const ErrorContainerBox = styled(Box)`
    grid-template-columns: 1fr 1fr;

    @media (min-width: ${breakpoints.small[0]}px) and (max-width: ${breakpoints.medium[1]}px) {
        grid-template-columns: 1fr;
    }
`;

const ErrorArtworkBox = styled(Box)`
    position: relative;
    grid-row: span 2;
`;

interface Props {
    errorCode: ErrorCodeKeys;
    error?: { message: string };
}

function FullscreenError({ errorCode, error }: Props) {
    const { displayErrors } = useAppConfig();
    // TODO See if this is a) necessary or b) another way to do this without using context
    const staticContext = useRouterContext();
    if (staticContext) {
        staticContext.statusCode = Number(errorCode);
    }

    return (
        <Page title={contentByErrorCode[errorCode].heading}>
            <Helmet
                bodyAttributes={{
                    class: 'max--desktop transparent-site-header',
                }}
            />
            <ContentBody maxWidth="desktop">
                <ErrorContainerBox display="grid" paddingX="1bu">
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Heading
                            accessibilityLevel={1}
                            withDivider
                            style={{
                                color: contentByErrorCode[errorCode].color,
                            }}
                            LEGACY_marginBottom="1.5bu"
                        >
                            {contentByErrorCode[errorCode].heading}
                        </Heading>
                        <p>{contentByErrorCode[errorCode].message}</p>
                        {error && displayErrors ? <SubtleText>{error.message}</SubtleText> : null}
                    </Box>
                    <ErrorArtworkBox margin="auto" maxWidth="60vw">
                        <DesignFigure
                            imageSource={contentByErrorCode[errorCode].image}
                            attribution={formatMessage(__('by {designer}'), {
                                designer: contentByErrorCode[errorCode].imageAttribution,
                            })}
                            variations={['attribution-right']}
                        />
                    </ErrorArtworkBox>
                    <div>
                        <ButtonContainer space="0.75bu">
                            <Button
                                href="/"
                                size="large"
                                width={['fill', 'auto', 'auto']}
                                appearance={createButtonPrimaryColorConfig({
                                    color: contentByErrorCode[errorCode].color,
                                    backgroundColorActive: contentByErrorCode[errorCode].color,
                                    backgroundColorHover: contentByErrorCode[errorCode].color,
                                })}
                            >
                                {__('Get me outta here')}
                            </Button>
                            <Button
                                href="/help"
                                size="large"
                                width={['fill', 'auto', 'fill']}
                                appearance={createButtonTertiaryColorConfig({
                                    color: contentByErrorCode[errorCode].color,
                                    backgroundColorHover: 'transparent',
                                    backgroundColorActive: 'transparent',
                                })}
                            >
                                {__('Help')}
                            </Button>
                        </ButtonContainer>
                    </div>
                </ErrorContainerBox>
            </ContentBody>
        </Page>
    );
}

export default FullscreenError;

export type ErrorCodeKeys = '403' | '404' | '429' | '500';

type ErrorContentByCodeType = {
    [key in ErrorCodeKeys]: {
        message: string;
        heading: string;
        image: string;
        imageAttribution: string;
        color: string;
    };
};

const contentByErrorCode: ErrorContentByCodeType = {
    403: {
        heading: __('Poopskies'),
        message: __('This is not where I want to be!'),
        image: Poopskies,
        imageAttribution: 'Figzy88',
        color: '#4DC0E3',
    },
    404: {
        heading: __('Lost your way?'),
        message: __('This is not where I want to be!'),
        image: LostDog,
        imageAttribution: 'ALINAsINK',
        color: '#F36C78',
    },
    429: {
        heading: __('Slow down!'),
        message: __(
            "We're experiencing a flood of traffic from the same IP address as your browser, try again later"
        ),
        image: ValentinoBaptista,
        imageAttribution: 'Valentino Baptista',
        color: '#DD533C',
    },
    500: {
        heading: __('Eeeeeeek!'),
        message: __(
            'It seems there was a problem. The 99designs development team has been notified and are likely addressing the problem at this very minute. Sit tight, grab a coffee, and hit refresh in a few minutes.'
        ),
        image: Elephant,
        imageAttribution: 'ganjarelex',
        color: '#C1B946',
    },
};
